import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { UnauthenticatedPage } from "pages/layouts";
import { GridLayout } from "components/grid";
import { FormTextInput } from "components/form-fields";
import { Button } from "components/buttons";
import { ShowHide } from "components/layout";
import { NotificationToaster, SuccessPanel } from "components/notifications";
import { useValidation } from "hooks/useValidation";
import { Recaptcha } from "components/recaptcha";
import { ReferrerService } from "services/referrerService";
import { Intent } from "@blueprintjs/core";

export function Referrer() {
    const validationErrorResponse = 400;
    const { code } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSuccessful, setIsSuccessful] = useState(false);
    const [referrerName, setReferrerName] = useState("");
    const [userFormDetails, setUserFormDetails] = useState({
        firstName: "",
        lastName: "",
        jobTitle: "",
        emailAddress: "",
        telephoneNumber: "",
        companyName: "",
        addressLine1: "",
        addressLine2: "",
        addressLine3: "",
        townAndCity: "",
        postcode: "",
        token: ""
    });
    const [isValid, errors, validate] = useValidation();

    // Recaptcha
    const [showCaptchaError, setShowCaptchaError] = useState(false);
    const captchaRef = useRef(null);

    useEffect(() => {
        if (!code) {
            return;
        }

        ReferrerService.getReferrerDetails(code).then(
            (response) => {
                if (response && response.active === false) {
                    window.location.href = `${window.env.VISION_URL}/enquire?referrer=expired`;
                    return;
                }
                setReferrerName(response.referrerName);
            },
            (error) => {
                if (error.response.status === validationErrorResponse) {
                    NotificationToaster.show(
                        Intent.WARNING,
                        error.response.data
                    );
                } else {
                    window.location.href = `${window.env.VISION_URL}/enquire?referrer=invalid`;
                }
            }
        ).catch((error) => {
            window.location.href = `${window.env.VISION_URL}/enquire?referrer=invalid`;
        }).finally(() => {
            setIsLoading(false);
        });
    }, [code]);

    useEffect(() => {
        let rules = [
            { fieldName: "firstName", required: true },
            { fieldName: "lastName", required: true },
            { fieldName: "jobTitle", required: true },
            { fieldName: "emailAddress", required: true, type: "email" },
            { fieldName: "telephoneNumber", required: true },
            { fieldName: "companyName", required: true },
            { fieldName: "addressLine1", required: true },
            { fieldName: "townAndCity", required: true },
            { fieldName: "postcode", required: true }
        ];

        validate(rules, userFormDetails);
    }, [userFormDetails]);

    function submitReferrerForm() {
        setIsSaving(true);

        const token = captchaRef.current.getValue();
        if (token === "") {
            setShowCaptchaError(true);
            setIsSaving(false);
            return;
        }

        setShowCaptchaError(false);

        userFormDetails.token = token;

        ReferrerService.submitReferrerForm(code, userFormDetails).then(
            (response) => {
                setIsSuccessful(true);
            },
            (error) => {
                NotificationToaster.show(Intent.DANGER, "An error occurred while submitting your details. Please try again.");
            }
        ).catch((error) => {
            NotificationToaster.show(Intent.DANGER, "An error occurred while submitting your details. Please try again.");
        }).finally(() => {
            setIsSaving(false);
        });
    }

    return (
        <UnauthenticatedPage>
            <div>
                <ShowHide
                    evaluator={isSuccessful}
                    show={<SuccessPanel>
                        <p className="spacer-bottom">
                            Your interest has been registered successfully. A member of the Vision team will contact you shortly to discuss. You may now close this window.
                        </p>

                    </SuccessPanel>}
                    hide={
                        <div>
                            <h2>Register your interest in Vision</h2>
                            <p className="spacer-bottom">{referrerName} has given you this link so that you can register an interest in Vision. Please fill in the form below and a member of the Logistics UK team will contact you regarding your enquiry. Our team aim to respond to enquiries within 48 business hours of it being submitted to us.</p>
                            <h3>Personal Details</h3>
                            <GridLayout numberOfColumns={3} gap={30}>
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="First Name/s"
                                    dangerHelperText={errors.firstName}
                                    value={userFormDetails.firstName}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, firstName: e.target.value })}
                                    fill
                                    maxLength={50}
                                />
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Last Name"
                                    dangerHelperText={errors.lastName}
                                    value={userFormDetails.lastName}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, lastName: e.target.value })}
                                    fill
                                    maxLength={50}
                                />
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Company Name"
                                    dangerHelperText={errors.companyName}
                                    value={userFormDetails.companyName}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, companyName: e.target.value })}
                                    fill
                                    maxLength={100}
                                />
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Job Title"
                                    dangerHelperText={errors.jobTitle}
                                    value={userFormDetails.jobTitle}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, jobTitle: e.target.value })}
                                    fill
                                    maxLength={100}
                                />
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Email address"
                                    dangerHelperText={errors.emailAddress}
                                    value={userFormDetails.emailAddress}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, emailAddress: e.target.value })}
                                    fill
                                    maxLength={100}
                                />
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Telephone Number"
                                    dangerHelperText={errors.telephoneNumber}
                                    value={userFormDetails.telephoneNumber}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, telephoneNumber: e.target.value })}
                                    fill
                                    maxLength={50}
                                />
                            </GridLayout>
                            <h3>Current Address</h3>
                            <GridLayout numberOfColumns={3} gap={40}>
                                <div>
                                    <FormTextInput
                                        loading={isLoading}
                                        disabled={isSaving}
                                        headingText="Address"
                                        placeholder="Line 1"
                                        dangerHelperText={errors.addressLine1}
                                        value={userFormDetails.addressLine1}
                                        onChange={(e) => setUserFormDetails({ ...userFormDetails, addressLine1: e.target.value })}
                                        fill
                                        maxLength={250}
                                    />
                                    <FormTextInput
                                        loading={isLoading}
                                        disabled={isSaving}
                                        placeholder="Line 2 (optional)"
                                        value={userFormDetails.addressLine2}
                                        onChange={(e) => setUserFormDetails({ ...userFormDetails, addressLine2: e.target.value })}
                                        fill
                                        maxLength={250}
                                    />
                                    <FormTextInput
                                        loading={isLoading}
                                        disabled={isSaving}
                                        placeholder="Line 3 (optional)"
                                        value={userFormDetails.addressLine3}
                                        onChange={(e) => setUserFormDetails({ ...userFormDetails, addressLine3: e.target.value })}
                                        fill
                                        maxLength={250}
                                    />
                                </div>
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Town/ City"
                                    dangerHelperText={errors.townAndCity}
                                    value={userFormDetails.townAndCity}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, townAndCity: e.target.value })}
                                    fill
                                    maxLength={80}
                                />
                                <FormTextInput
                                    loading={isLoading}
                                    disabled={isSaving}
                                    headingText="Postcode"
                                    dangerHelperText={errors.postcode}
                                    value={userFormDetails.postcode}
                                    onChange={(e) => setUserFormDetails({ ...userFormDetails, postcode: e.target.value })}
                                    fill
                                    maxLength={20}
                                />
                            </GridLayout>
                            <Recaptcha showError={showCaptchaError} captchaRef={captchaRef} />
                            <Button
                                loading={isLoading}
                                disabled={isSaving || !isValid}
                                intent="primary"
                                text={"Submit"}
                                onClick={submitReferrerForm}
                            />
                            <p className="spacer-top">
                                Please note: the details you submit, including your email address/phone number, may be used to keep you informed about Logistics UK’s products/services/events. If you do not want to receive information of this nature, please email <a href="mailto:salessupport@logistics.org.uk">salessupport@logistics.org.uk</a>
                            </p>
                        </div>}
                />
            </div>
        </UnauthenticatedPage>
    );
}